import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState } from 'react';
import { verifyAdminStatus, refreshAdminStatus } from '../services/adminVerification';
import { logAdminStatus } from '../utils/adminDebug';
import { useAdminStatus } from '../hooks/useAdminStatus';

/**
 * Admin Route Component
 * Protects routes that require admin role
 * Redirects to account page if not admin
 * Logs unauthorized access attempts
 */
export default function AdminRoute({ children }) {
  const { user, profile, isLoading } = useAuth();
  const { isAdmin: hookIsAdmin, isLoading: hookIsLoading } = useAdminStatus();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [verificationError, setVerificationError] = useState(null);

  // Verify admin role on mount and when profile changes
  useEffect(() => {
    const verifyAdmin = async () => {
      if (isLoading || hookIsLoading) {
        setIsVerifying(true);
        return;
      }

      if (!user) {
        setIsVerifying(false);
        setIsAdmin(false);
        return;
      }

      // Use profile role as optimistic fallback
      const profileIsAdmin = profile?.role === 'admin';
      
      // Use centralized verification service (always checks database)
      try {
        console.log('[AdminRoute] Verifying admin status for user:', user.id);
        const result = await verifyAdminStatus(user.id);

        if (result.error) {
          console.error('[AdminRoute] Error verifying admin status:', {
            error: result.error,
            userId: user.id,
            profileRole: profile?.role,
            hookIsAdmin,
          });
          
          // If verification fails but profile says admin, use profile as fallback
          if (profileIsAdmin || hookIsAdmin) {
            console.warn('[AdminRoute] Verification failed but profile/hook indicates admin, allowing access');
            setIsAdmin(true);
            logAdminStatus(user.id, true, 'admin_route_fallback');
          } else {
            setIsAdmin(false);
            setVerificationError(result.error);
            logAdminStatus(user.id, false, 'admin_route_error');
          }
        } else {
          const userIsAdmin = result.isAdmin;
          setIsAdmin(userIsAdmin);
          setVerificationError(null);
          logAdminStatus(user.id, userIsAdmin, 'admin_route');

          if (!userIsAdmin) {
            // Log unauthorized access attempt
            console.warn(
              `⚠️ [AdminRoute] Unauthorized admin access attempt by user: ${user.email} (ID: ${user.id})`
            );
          } else {
            console.log('[AdminRoute] ✅ Admin access granted');
          }
        }
      } catch (error) {
        console.error('[AdminRoute] Error verifying admin role:', {
          error: error.message || error,
          code: error.code,
          userId: user.id,
          profileRole: profile?.role,
          hookIsAdmin,
        });
        
        // If verification throws but profile says admin, use profile as fallback
        if (profileIsAdmin || hookIsAdmin) {
          console.warn('[AdminRoute] Verification exception but profile/hook indicates admin, allowing access');
          setIsAdmin(true);
          logAdminStatus(user.id, true, 'admin_route_exception_fallback');
        } else {
          setIsAdmin(false);
          setVerificationError(error.message || 'Verification failed');
          logAdminStatus(user.id, false, 'admin_route_exception');
        }
      } finally {
        setIsVerifying(false);
      }
    };

    verifyAdmin();
  }, [user, profile, isLoading, hookIsLoading, hookIsAdmin]);

  // Retry verification if it fails (with exponential backoff)
  useEffect(() => {
    if (!user || isLoading || hookIsLoading || isAdmin) return;

    // Check if profile or hook says admin (optimistic check)
    const profileIsAdmin = profile?.role === 'admin';
    if (profileIsAdmin || hookIsAdmin) {
      console.log('[AdminRoute] Profile/hook indicates admin, allowing access while retrying verification');
      setIsAdmin(true);
      return;
    }

    // Retry after 2 seconds if verification failed
    const retryTimer = setTimeout(async () => {
      console.log('[AdminRoute] Retrying admin verification...');
      try {
        const result = await refreshAdminStatus(user.id);
        if (result.isAdmin) {
          setIsAdmin(true);
          setVerificationError(null);
          logAdminStatus(user.id, true, 'admin_route_retry');
        } else if (result.error) {
          console.error('[AdminRoute] Retry verification error:', result.error);
          // Still check profile as fallback
          if (profileIsAdmin || hookIsAdmin) {
            setIsAdmin(true);
            console.warn('[AdminRoute] Retry failed but profile/hook indicates admin, allowing access');
          }
        }
      } catch (error) {
        console.error('[AdminRoute] Retry failed:', error);
        // Check profile as fallback
        if (profileIsAdmin || hookIsAdmin) {
          setIsAdmin(true);
          console.warn('[AdminRoute] Retry exception but profile/hook indicates admin, allowing access');
        }
      }
    }, 2000);

    return () => clearTimeout(retryTimer);
  }, [user, isLoading, hookIsLoading, isAdmin, profile?.role, hookIsAdmin]);

  // Show loading state while checking authentication and role
  if (isLoading || isVerifying || hookIsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying admin access...</p>
          {verificationError && (
            <p className="text-sm text-red-600 mt-2">Retrying verification...</p>
          )}
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is admin - use multiple sources
  const finalIsAdmin = isAdmin || profile?.role === 'admin' || hookIsAdmin;
  
  if (!finalIsAdmin) {
    console.warn('[AdminRoute] Access denied - redirecting to account page', {
      userId: user.id,
      isAdmin,
      profileRole: profile?.role,
      hookIsAdmin,
      verificationError,
    });
    // Redirect non-admins to their account page
    return <Navigate to="/account" replace />;
  }

  console.log('[AdminRoute] ✅ Admin access granted, rendering admin dashboard');

  // Render admin content
  return children;
}
